import type { PlayerFamilyProfileOption, PlayerFamilyProfileSummary } from "./api";

/**
 * Perfil activo de una cuenta familiar (un tutor con varios hijos).
 *
 * UNA sola fuente de verdad (bug de Eduardo 2026-09-29: cambiaba a Axel en
 * el home y al entrar al torneo volvía a salir Abigail): el id se guarda por
 * cuenta en localStorage y cada cambio avisa a todas las pantallas abiertas
 * — en la misma pestaña con un evento propio y en otras pestañas con el
 * evento `storage` del navegador. Las pantallas no leen localStorage por su
 * cuenta: usan `usePlayerProfileScope` (lib/usePlayerProfileScope.ts).
 */

export const ACTIVE_FAMILY_PROFILE_KEY_PREFIX = "torneos123_active_family_profile_";
/** Evento de la misma pestaña (el `storage` del navegador solo llega a las otras). */
export const ACTIVE_FAMILY_PROFILE_EVENT = "torneos123:active-family-profile";

export function activeFamilyProfileKey(userId?: number | string | null): string {
  return `${ACTIVE_FAMILY_PROFILE_KEY_PREFIX}${userId ?? "anon"}`;
}

function parseProfileId(raw: string | null | undefined): number | null {
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Elección hecha en ESTA pestaña que el navegador no dejó guardar (modo
 * privado, almacenamiento bloqueado o lleno). Sin esto el selector «no hacía
 * nada»: se releía localStorage y volvía el hijo anterior. Dura lo que la
 * pestaña; en cuanto localStorage acepta un valor, vuelve a mandar él.
 */
const unsavedActiveProfile = new Map<string, number>();

function readStoredProfileId(key: string): number | null {
  try {
    return parseProfileId(window.localStorage.getItem(key));
  } catch {
    // Navegación privada / almacenamiento bloqueado: sin perfil guardado.
    return null;
  }
}

export function readActiveFamilyProfileId(userId?: number | string | null): number | null {
  if (typeof window === "undefined") return null;
  const key = activeFamilyProfileKey(userId);
  return unsavedActiveProfile.get(key) ?? readStoredProfileId(key);
}

export function writeActiveFamilyProfileId(
  userId: number | string | null | undefined,
  profileId: number | null | undefined,
): void {
  if (typeof window === "undefined" || !profileId) return;
  const key = activeFamilyProfileKey(userId);
  const previous = readActiveFamilyProfileId(userId);
  try {
    window.localStorage.setItem(key, String(profileId));
    unsavedActiveProfile.delete(key);
  } catch {
    // El navegador no lo guardó: se recuerda en memoria para esta pestaña.
    unsavedActiveProfile.set(key, profileId);
  }
  if (previous === profileId) return;
  window.dispatchEvent(new CustomEvent(ACTIVE_FAMILY_PROFILE_EVENT, { detail: { key, profileId } }));
}

/** Olvida el perfil activo guardado de la cuenta (ya no le queda ese perfil). */
export function clearActiveFamilyProfileId(userId: number | string | null | undefined): void {
  if (typeof window === "undefined") return;
  const key = activeFamilyProfileKey(userId);
  unsavedActiveProfile.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Almacenamiento bloqueado: no había nada guardado.
  }
  window.dispatchEvent(new CustomEvent(ACTIVE_FAMILY_PROFILE_EVENT, { detail: { key, profileId: null } }));
}

/** Solo para pruebas: olvida las elecciones que no se pudieron guardar. */
export function resetUnsavedActiveProfilesForTests(): void {
  unsavedActiveProfile.clear();
}

/** Escucha cambios del perfil activo de ESA cuenta (misma pestaña y otras). */
export function subscribeActiveFamilyProfile(
  userId: number | string | null | undefined,
  onChange: () => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const key = activeFamilyProfileKey(userId);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === key) {
      // Otra pestaña sí pudo guardar: su valor manda sobre el de memoria.
      unsavedActiveProfile.delete(key);
      onChange();
    }
  };
  const onLocal = (event: Event) => {
    const detail = (event as CustomEvent<{ key?: string }>).detail;
    if (!detail || detail.key === key) onChange();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(ACTIVE_FAMILY_PROFILE_EVENT, onLocal);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(ACTIVE_FAMILY_PROFILE_EVENT, onLocal);
  };
}

export function selectedFamilyProfileId(
  familyProfiles: PlayerFamilyProfileOption[],
  activeProfileId: number | null,
  fallbackProfileId?: number | null,
): number | null {
  if (activeProfileId && familyProfiles.some((profile) => profile.id === activeProfileId)) {
    return activeProfileId;
  }
  return fallbackProfileId ?? familyProfiles[0]?.id ?? null;
}

/** Perfil por omisión: el de id más bajo, el mismo que elige el servidor. */
export function lowestProfileId(profiles: Array<{ id: number }>): number | null {
  let lowest: number | null = null;
  for (const profile of profiles) {
    if (lowest === null || profile.id < lowest) lowest = profile.id;
  }
  return lowest;
}

/** Perfiles de la familia inscritos (inscripción activa) en ese torneo. */
export function familyProfilesInTournament(
  profiles: PlayerFamilyProfileSummary[],
  tournamentId: number,
): PlayerFamilyProfileSummary[] {
  return profiles.filter((profile) => (profile.tournament_ids ?? []).includes(tournamentId));
}

export interface ProfileScopeInput {
  /** Perfiles de la cuenta (null = todavía no se sabe). */
  familyProfiles: PlayerFamilyProfileSummary[] | null;
  /** Ya terminó la carga de la lista (bien o con error). */
  loaded: boolean;
  /** Perfil activo guardado por la cuenta. */
  activeProfileId: number | null;
  /** Pantalla de un torneo: solo cuentan los perfiles inscritos en él. */
  tournamentId?: number | null;
  /** Perfil que trae la liga (p. ej. «Reportar resultado» desde un torneo). */
  preferredProfileId?: number | null;
  /** La última carga de la lista falló (sin red, error del servidor). */
  listFailed?: boolean;
  /** El torneo no aparece en una lista cargada ANTES de abrir la pantalla
   * (p. ej. el hijo se acaba de inscribir): se está volviendo a pedir. */
  awaitingRefresh?: boolean;
}

export interface ProfileScopeResolution {
  /** Perfiles entre los que se puede cambiar en esta pantalla. */
  profiles: PlayerFamilyProfileSummary[];
  /** Perfil con el que se piden los datos (null = que decida el servidor). */
  profileId: number | null;
  /** Ya se sabe qué perfil usar: la pantalla puede pedir sus datos. */
  ready: boolean;
}

/**
 * Qué perfil usa una pantalla. Puro (sin React) para poder probarlo.
 *
 * - Pantalla general: el que trae la liga, si no el activo, si no el de id
 *   más bajo (el mismo del servidor). Con un perfil activo guardado no se
 *   espera a la lista: se usa de inmediato y el servidor lo valida.
 * - Pantalla de un torneo: solo los perfiles INSCRITOS en él. El activo si
 *   juega el torneo; si no, el primero inscrito — sin tocar el activo
 *   guardado (eso solo lo cambia el jugador con el selector). Espera a la
 *   lista (y a la recarga si el torneo no venía en una lista vieja). Si la
 *   lista no se pudo cargar, usa el perfil guardado (el servidor lo valida)
 *   para no volver al primer hijo; si la lista dice que nadie de la familia
 *   juega el torneo, deja que el servidor elija.
 */
export function resolveProfileScope(input: ProfileScopeInput): ProfileScopeResolution {
  const family = input.familyProfiles ?? [];
  const known = input.familyProfiles !== null;
  const isValid = (id: number | null | undefined, pool: Array<{ id: number }>): id is number =>
    Boolean(id) && pool.some((profile) => profile.id === id);

  if (input.tournamentId != null && Number.isFinite(input.tournamentId)) {
    const inTournament = familyProfilesInTournament(family, input.tournamentId);
    if (!input.loaded && !known) {
      return { profiles: [], profileId: null, ready: false };
    }
    if (inTournament.length === 0 && input.awaitingRefresh) {
      return { profiles: [], profileId: null, ready: false };
    }
    if (inTournament.length === 0) {
      if (input.listFailed || !known) {
        const saved = input.preferredProfileId || input.activeProfileId || null;
        return { profiles: [], profileId: saved, ready: true };
      }
      return { profiles: [], profileId: null, ready: true };
    }
    const profileId = isValid(input.preferredProfileId, inTournament)
      ? input.preferredProfileId
      : isValid(input.activeProfileId, inTournament)
        ? input.activeProfileId
        : lowestProfileId(inTournament);
    return { profiles: inTournament, profileId, ready: true };
  }

  if (known) {
    if (family.length === 0) return { profiles: [], profileId: null, ready: true };
    const profileId = isValid(input.preferredProfileId, family)
      ? input.preferredProfileId
      : isValid(input.activeProfileId, family)
        ? input.activeProfileId
        : lowestProfileId(family);
    return { profiles: family, profileId, ready: true };
  }
  const optimistic = input.preferredProfileId || input.activeProfileId || null;
  if (optimistic) {
    return { profiles: family, profileId: optimistic, ready: true };
  }
  return { profiles: family, profileId: null, ready: input.loaded };
}
